import React, { useState, useEffect } from "react";

const BMRCalculator = () => {
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [gender, setGender] = useState("male");
  const [unitSystem, setUnitSystem] = useState("metric");
  const [age, setAge] = useState(25);
  const [activityLevel, setActivityLevel] = useState("sedentary");
  const [result, setResult] = useState({
    bmrValue: "0",
    tdeeValue: "0",
    interpretation: "",
    calorieBreakdown: "",
  });

  // BMR Calculation using Mifflin-St Jeor Equation
  const calculateBMR = () => {
    let w = parseFloat(weight);
    let h = parseFloat(height);
    let a = parseFloat(age);

    if (isNaN(w) || w <= 0 || isNaN(h) || h <= 0 || isNaN(a) || a <= 0) return;

    let bmr;
    if (unitSystem === "metric") {
      // Mifflin-St Jeor Equation (Metric)
      if (gender === "male") {
        bmr = 10 * w + 6.25 * h - 5 * a + 5;
      } else {
        bmr = 10 * w + 6.25 * h - 5 * a - 161;
      }
    } else {
      // Convert imperial to metric first
      const weightKg = w * 0.453592;
      const heightCm = h * 2.54;
      if (gender === "male") {
        bmr = 10 * weightKg + 6.25 * heightCm - 5 * a + 5;
      } else {
        bmr = 10 * weightKg + 6.25 * heightCm - 5 * a - 161;
      }
    }

    // Calculate TDEE based on activity level
    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      veryActive: 1.9,
    };

    const tdee = bmr * activityMultipliers[activityLevel];

    let interpretation = "";
    if (gender === "male") {
      interpretation =
        "Your BMR represents the calories your body burns at complete rest. This is the minimum energy needed for vital functions like breathing, circulation, and cell production.";
    } else {
      interpretation =
        "Your BMR represents the calories your body burns at complete rest. Women typically have a lower BMR than men due to differences in muscle mass and body composition.";
    }

    const calorieBreakdown = `To maintain: ${Math.round(tdee)} cal/day | To lose weight: ${Math.round(tdee - 500)} cal/day | To gain weight: ${Math.round(tdee + 500)} cal/day`;

    setResult({
      bmrValue: Math.round(bmr).toString(),
      tdeeValue: Math.round(tdee).toString(),
      interpretation,
      calorieBreakdown,
    });
  };

  useEffect(() => {
    calculateBMR();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
            </svg>
          </div>
          <h1>BMR Calculator</h1>
          <p>
            Calculate your Basal Metabolic Rate (BMR) to understand how many calories your body burns at rest and determine your daily caloric needs based on activity level.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                BMR Inputs
              </h2>

              {/* Weight Input */}
              <div className="input-group">
                <label>Weight ({unitSystem === "metric" ? "kg" : "lbs"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⚖️</span>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 60, 70, 80, 90].map((val) => (
                    <button
                      key={val}
                      onClick={() => setWeight(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height Input */}
              <div className="input-group">
                <label>Height ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📏</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="calc-input"
                    placeholder="170"
                  />
                </div>
                <div className="quick-select-group">
                  {[150, 160, 170, 180, 190].map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeight(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age */}
              <div className="input-group">
                <label>Age</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activity Level */}
              <div className="input-group">
                <label>Activity Level</label>
                <div className="quick-select-group" style={{ flexDirection: "column", gap: "0.5rem" }}>
                  {[
                    { value: "sedentary", label: "Sedentary (little/no exercise)" },
                    { value: "light", label: "Light (exercise 1-3 days/week)" },
                    { value: "moderate", label: "Moderate (exercise 3-5 days/week)" },
                    { value: "active", label: "Active (exercise 6-7 days/week)" },
                    { value: "veryActive", label: "Very Active (intense daily exercise)" },
                  ].map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setActivityLevel(level.value)}
                      className={`quick-select-btn ${activityLevel === level.value ? "primary" : "secondary"}`}
                      style={{ width: "100%", textAlign: "left" }}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unit System */}
              <div className="input-group">
                <label>Measurement System</label>
                <div className="quick-select-group">
                  {[
                    { value: "metric", label: "Metric (kg/cm)" },
                    { value: "imperial", label: "Imperial (lbs/in)" },
                  ].map((unit) => (
                    <button
                      key={unit.value}
                      onClick={() => setUnitSystem(unit.value)}
                      className={`quick-select-btn ${unitSystem === unit.value ? "primary" : "secondary"}`}
                    >
                      {unit.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateBMR} className="calculate-btn">
                Calculate BMR
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                BMR Result
              </h2>

              <div className="result-card">
                {/* BMR Value */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Your BMR</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.bmrValue}</div>
                  <div className="result-subtext">calories/day at rest</div>
                </div>

                {/* TDEE Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Daily Calorie Needs (TDEE)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.tdeeValue}</div>
                  <div className="result-subtext">calories/day with activity</div>
                </div>

                {/* Calorie Breakdown */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Calorie Goals</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500" }}>
                    {result.calorieBreakdown}
                  </p>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Interpretation</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.interpretation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding BMR</h3>
              <p>
                Basal Metabolic Rate (BMR) is the number of calories your body needs to perform basic life-sustaining functions at rest, such as breathing, circulation, nutrient processing, and cell production. 
                Your Total Daily Energy Expenditure (TDEE) accounts for your activity level and represents the total calories you burn each day. Understanding these numbers helps you create effective nutrition plans for weight management.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>BMR Calculation Formula (Mifflin-St Jeor):</h4>
            <div className="formula-display">
              Men: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age + 5
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Women: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age - 161
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Sedentary</div>
                <div className="formula-param-value">BMR × 1.2</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Light Activity</div>
                <div className="formula-param-value">BMR × 1.375</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Moderate Activity</div>
                <div className="formula-param-value">BMR × 1.55</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Very Active</div>
                <div className="formula-param-value">BMR × 1.9</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is BMR?</h5>
              <p>
                BMR is the minimum number of calories your body needs to function at rest, covering basic physiological processes like breathing and circulation.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between BMR and TDEE?</h5>
              <p>
                BMR is calories burned at rest, while TDEE includes calories burned through daily activities and exercise. TDEE = BMR × Activity Factor.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I use BMR for weight loss?</h5>
              <p>
                To lose weight, consume fewer calories than your TDEE (typically 300-500 calories less). Never eat below your BMR as it can slow metabolism and harm health.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does BMR change with age?</h5>
              <p>
                Yes, BMR typically decreases with age due to loss of muscle mass. Strength training and staying active can help maintain a higher BMR.
              </p>
            </div>

            <div className="faq-item">
              <h5>What factors affect BMR?</h5>
              <p>
                BMR is influenced by age, gender, body composition, genetics, hormones, and muscle mass. Men typically have higher BMR than women due to more muscle mass.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BMRCalculator;